import { Link, Path } from "@/router";
import MetricCard from "../MetricCard/MetricCard";
import { MetricCardType } from "../MetricCard/MetricCard.types";
import { getMetricGridColumnConfig, type MetricGridLayout } from "./metricGridLayout";
import { useMetricGridBalancedColumns } from "./useMetricGridBalancedColumns";

interface MetricGridProps<P extends Path> {
  metrics: MetricCardType<P>[];
  layout?: MetricGridLayout;
  /** When set, columns use a minimum card width (from `md` up) and balance wrapped rows. Ignores `layout`. */
  minCardWidth?: number;
  hasHeading?: boolean;
}

export default function MetricGrid<P extends Path>({
  metrics,
  layout = "balanced",
  minCardWidth,
  hasHeading = false
}: MetricGridProps<P>) {
  const { gridRef, balancedColumnCount, isMdUp } = useMetricGridBalancedColumns(metrics.length, minCardWidth);
  const { className: columnClassName, style } = getMetricGridColumnConfig({
    layout,
    minCardWidth,
    balancedColumnCount,
    isMdUp
  });

  if (metrics.length === 0) {
    return null;
  }

  return (
    <div
      ref={gridRef}
      className={`grid gap-3 items-stretch auto-rows-fr ${!hasHeading ? "mt-12" : ""} ${columnClassName}`}
      style={style}
      data-testid="metrics"
    >
      {metrics.map(({ link, ...props }, i) =>
        link ? (
          <Link key={i} className="block h-full min-w-0 w-full" {...link}>
            <MetricCard link={link} {...props} />
          </Link>
        ) : (
          <MetricCard key={i} {...props} />
        )
      )}
    </div>
  );
}
